import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { PageModalContextValue, PageModalProps } from './types';

const PageModalContext = createContext<PageModalContextValue | null>(null);

export function usePageModal(): PageModalContextValue {
  const ctx = useContext(PageModalContext);
  if (!ctx) {
    throw new Error('usePageModal must be used within a <PageModal>');
  }
  return ctx;
}

/**
 * Holds the open/close state and exposes it via context. Shared by every
 * platform's PageModal root — including platforms where the modal itself is
 * unimplemented — so {@link usePageModal} always resolves and honours each
 * platform's rendered parts rather than throwing on unsupported platforms.
 *
 * Free of any platform UI dependency (e.g. Headless UI) so it can back the
 * unimplemented variants without pulling their peer deps.
 */
export function PageModalProvider(props: PageModalProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  const value = useMemo<PageModalContextValue>(
    () => ({ isOpen, open, close, toggle }),
    [isOpen, open, close, toggle],
  );

  return <PageModalContext.Provider value={value}>{props.children}</PageModalContext.Provider>;
}
